import { useState } from 'react';
import type { FormEvent } from 'react';
import { newIdempotencyKey } from '../api/client';
import type { AppConnection, ConnectionAssign, EventSubscription, Problem } from '../api/types';
import { Badge } from '../components/Badge';
import type { BadgeTone } from '../components/Badge';
import { Button } from '../components/Button';
import { EmptyState } from '../components/EmptyState';
import { ProblemNotice } from '../components/ProblemNotice';
import { Spinner } from '../components/Spinner';
import { Table } from '../components/Table';
import { usePlatform } from '../app/platform';
import { toProblem, useAsync } from '../app/useAsync';
import { formatDateTime } from '../lib/datetime';

export interface ConnectionsTabProps {
  workspaceId: string;
  appId: string;
  editable: boolean;
}

/**
 * microapp の接続先（コードが宣言した名前）ごとに provider の設定を入れる。
 * token はここで受け取って基盤に保存し、読み戻さない（入っているかどうかだけ hasXxx で返る）。
 * Event の購読は receive 許可付きで設定したときに有効になる（未設定は disabled のまま残る）。
 */
export function ConnectionsTab({ workspaceId, appId, editable }: ConnectionsTabProps) {
  const { api } = usePlatform();
  const listing = useAsync(
    () => api.listConnections(workspaceId, appId),
    [api, workspaceId, appId],
  );
  const [submitting, setSubmitting] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);
  /** フォームを開いている接続先の名前。同時に開くのは 1 枚だけ。 */
  const [editing, setEditing] = useState<string | null>(null);

  const run = async (action: () => Promise<unknown>) => {
    setSubmitting(true);
    setProblem(null);
    try {
      await action();
      setEditing(null);
      listing.reload();
    } catch (error) {
      setProblem(toProblem(error));
    } finally {
      setSubmitting(false);
    }
  };

  if (listing.loading) return <Spinner label="接続先を取得しています" />;
  if (listing.problem)
    return (
      <ProblemNotice
        problem={listing.problem}
        action={<Button onClick={listing.reload}>再取得</Button>}
      />
    );
  if (!listing.data) return null;

  return (
    <div className="connections-tab">
      <ProblemNotice problem={problem} />
      {listing.data.connections.length === 0 ? (
        <EmptyState
          title="接続先の宣言がありません"
          description="server/app.py の MicroApplication.connections で宣言した名前がここに並びます。"
        />
      ) : (
        listing.data.connections.map((connection) => (
          <ConnectionCard
            key={connection.name}
            connection={connection}
            open={editing === connection.name}
            disabled={!editable || submitting}
            onOpen={() => {
              setProblem(null);
              setEditing(connection.name);
            }}
            onClose={() => setEditing(null)}
            onSave={(body) =>
              void run(() =>
                api.assignConnection(workspaceId, appId, connection.name, body, {
                  idempotencyKey: newIdempotencyKey(),
                }),
              )
            }
            onRelease={() =>
              void run(() =>
                api.unassignConnection(workspaceId, appId, connection.name, {
                  idempotencyKey: newIdempotencyKey(),
                }),
              )
            }
          />
        ))
      )}
      <SubscriptionList subscriptions={listing.data.subscriptions} />
    </div>
  );
}

interface ConnectionCardProps {
  connection: AppConnection;
  open: boolean;
  disabled: boolean;
  onOpen(): void;
  onClose(): void;
  onSave(body: ConnectionAssign): void;
  onRelease(): void;
}

/** 宣言された名前 1 つ分のカード。見出し・現在の設定・Broker の状態と、開いたときの設定フォーム。 */
function ConnectionCard({
  connection,
  open,
  disabled,
  onOpen,
  onClose,
  onSave,
  onRelease,
}: ConnectionCardProps) {
  return (
    <section className="connection-card" aria-label={`接続先 ${connection.name}`}>
      <div className="connection-card-head">
        <h3>
          <code>{connection.name}</code>{' '}
          <Badge tone={connection.configured ? 'completed' : 'awaiting'}>
            {connection.configured ? '設定済み' : '未設定'}
          </Badge>
        </h3>
        <div className="card-actions">
          {open ? (
            <Button disabled={disabled} onClick={onClose}>
              閉じる
            </Button>
          ) : (
            <Button disabled={disabled} onClick={onOpen}>
              設定
            </Button>
          )}
          {connection.configured && (
            <Button variant="danger" disabled={disabled} onClick={onRelease}>
              解除
            </Button>
          )}
        </div>
      </div>
      <p className="hint">
        provider: {connection.provider} / 必要な許可: {connection.permissions.join(', ')}
        {connection.configured && ` / 受け方: ${connection.socketMode ? 'Socket Mode' : 'HTTP'}`}
        {connection.configured &&
          connection.grantedPermissions.length > 0 &&
          ` / 許可: ${connection.grantedPermissions.join(', ')}`}
        {connection.configured && connection.teamId && ` / team: ${connection.teamId}`}
        {connection.configured && connection.botUserId && ` / bot user: ${connection.botUserId}`}
        {connection.configured && ` / 対象チャネル: ${connection.target.channelId ?? '（すべて）'}`}
      </p>
      <BrokerStatus connection={connection} />
      {open && <ConnectionForm connection={connection} disabled={disabled} onSubmit={onSave} />}
    </section>
  );
}

/** Broker が観測した状態。設定の正本ではないので、無ければ何も出さない。 */
function BrokerStatus({ connection }: { connection: AppConnection }) {
  const { state, lastReceivedAt, error } = connection.status;
  if (!state && !lastReceivedAt && !error) return null;
  return (
    <p className="hint connection-status">
      {state && (
        <>
          状態: <Badge tone={statusTone(state)}>{state}</Badge>{' '}
        </>
      )}
      {lastReceivedAt && `最終受信: ${formatDateTime(lastReceivedAt)} `}
      {error && <span className="connection-status-error">{error}</span>}
    </p>
  );
}

function statusTone(state: string): BadgeTone {
  if (state === 'connected') return 'completed';
  if (state === 'connecting') return 'active';
  if (state === 'disconnected' || state === 'config_error') return 'failed';
  return 'neutral';
}

interface ConnectionFormProps {
  connection: AppConnection;
  disabled: boolean;
  onSubmit(body: ConnectionAssign): void;
}

/**
 * 接続先の設定フォーム。token 欄は password 型で、既存の値は表示しない
 * （空のまま保存すると「変えない」）。初回は Bot token が無いと保存できない。
 */
function ConnectionForm({ connection, disabled, onSubmit }: ConnectionFormProps) {
  const [socketMode, setSocketMode] = useState(connection.socketMode);
  const [appToken, setAppToken] = useState('');
  const [signingSecret, setSigningSecret] = useState('');
  const [botToken, setBotToken] = useState('');
  const [teamId, setTeamId] = useState(connection.teamId);
  const [botUserId, setBotUserId] = useState(connection.botUserId);
  const [channelId, setChannelId] = useState(connection.target.channelId ?? '');
  // 未設定のうちは宣言どおりの許可を初期値にする。設定済みなら現在の許可。
  const [permissions, setPermissions] = useState<string[]>(
    connection.configured ? connection.grantedPermissions : connection.permissions,
  );
  const prefix = `connection-${connection.name}`;
  const canSubmit = !disabled && (connection.hasBotToken || botToken.trim() !== '');

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!canSubmit) return;
    const trimmedChannel = channelId.trim();
    onSubmit({
      socketMode,
      appToken: appToken.trim(),
      signingSecret: signingSecret.trim(),
      botToken: botToken.trim(),
      teamId: teamId.trim(),
      botUserId: botUserId.trim(),
      target: trimmedChannel ? { channelId: trimmedChannel } : {},
      // 宣言の順に並べ、宣言に無い許可は送らない。
      permissions: connection.permissions.filter((permission) => permissions.includes(permission)),
    });
  }

  const togglePermission = (permission: string, checked: boolean) =>
    setPermissions((current) =>
      checked ? [...current, permission] : current.filter((item) => item !== permission),
    );

  const field = (
    id: string,
    label: string,
    value: string,
    set: (value: string) => void,
    extra: Record<string, unknown> = {},
    hint?: string,
  ) => (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="field-input"
        value={value}
        disabled={disabled}
        onChange={(event) => set(event.target.value)}
        {...extra}
      />
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
  const secret = { type: 'password', autoComplete: 'off' };
  const keptHint = (has: boolean) => (has ? '設定済み（空のまま保存すると変えない）' : undefined);

  return (
    <form className="form connection-form" onSubmit={handleSubmit} noValidate>
      <div className="field">
        <label className="field-label" htmlFor={`${prefix}-socket-mode`}>
          受け方
        </label>
        <select
          id={`${prefix}-socket-mode`}
          className="field-input"
          value={socketMode ? 'socket' : 'http'}
          disabled={disabled}
          onChange={(event) => setSocketMode(event.target.value === 'socket')}
        >
          <option value="socket">Socket Mode（Broker が WebSocket で受信）</option>
          <option value="http">HTTP（Slack が Request URL に POST）</option>
        </select>
      </div>
      {socketMode
        ? field(
            `${prefix}-app-token`,
            'App-Level Token（xapp-…、connections:write）',
            appToken,
            setAppToken,
            secret,
            keptHint(connection.hasAppToken),
          )
        : field(
            `${prefix}-signing-secret`,
            'Signing Secret',
            signingSecret,
            setSigningSecret,
            secret,
            keptHint(connection.hasSigningSecret),
          )}
      {!socketMode && connection.requestPath && (
        <p className="hint">
          Request URL の path: <code>{connection.requestPath}</code>（Slack App の Event
          Subscriptions / Interactivity に、Broker の origin と合わせて登録する）
        </p>
      )}
      {field(
        `${prefix}-bot-token`,
        'Bot token（xoxb-…）',
        botToken,
        setBotToken,
        { ...secret, required: !connection.hasBotToken },
        keptHint(connection.hasBotToken) ?? '未設定。保存には必要です',
      )}
      {field(
        `${prefix}-team-id`,
        'team ID（任意。空なら変えない。未設定なら接続時に auth.test で補完）',
        teamId,
        setTeamId,
      )}
      {field(
        `${prefix}-bot-user-id`,
        'bot user ID（任意。空なら変えない。未設定なら接続時に補完）',
        botUserId,
        setBotUserId,
      )}
      {field(
        `${prefix}-channel`,
        '対象チャネル ID（空なら bot が届く範囲すべて）',
        channelId,
        setChannelId,
        { placeholder: 'C0123456789' },
      )}
      <fieldset className="field connection-permissions">
        <legend className="field-label">許可（宣言の範囲内）</legend>
        {connection.permissions.map((permission) => {
          const id = `${prefix}-permission-${permission}`;
          return (
            <label key={permission} htmlFor={id} className="connection-permission">
              <input
                id={id}
                className="field-input"
                type="checkbox"
                checked={permissions.includes(permission)}
                disabled={disabled}
                onChange={(event) => togglePermission(permission, event.target.checked)}
              />
              {permission}
            </label>
          );
        })}
      </fieldset>
      <button type="submit" className="form-submit" disabled={!canSubmit}>
        保存
      </button>
    </form>
  );
}

function SubscriptionList({ subscriptions }: { subscriptions: EventSubscription[] }) {
  if (subscriptions.length === 0) return null;
  return (
    <section>
      <h3>Event の購読</h3>
      <Table
        label="Event の購読"
        columns={[
          { key: 'handler', header: 'ハンドラ' },
          { key: 'connection', header: '接続先' },
          { key: 'kind', header: '種別' },
          { key: 'target', header: '対象' },
          { key: 'start', header: '開始イベント' },
          { key: 'enabled', header: '状態' },
        ]}
        rows={subscriptions.map((subscription) => ({
          key: subscription.id,
          cells: {
            handler: <code>{subscription.handler}</code>,
            connection: subscription.connectionName,
            kind: subscription.kind,
            target: subscription.target.channelId ?? '（すべて）',
            start: `${subscription.processKey} / ${subscription.startEventId}`,
            enabled: (
              <Badge tone={subscription.enabled ? 'completed' : 'awaiting'}>
                {subscription.enabled ? '受信中' : '無効（未設定）'}
              </Badge>
            ),
          },
        }))}
      />
    </section>
  );
}
